import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';

import * as authApi from '../api/auth';

/**
 * Forgot-password kickoff page — ROADMAP #18.1.
 *
 * Single-field form. On submit, regardless of whether the email
 * actually maps to an account, the page swaps to a confirmation
 * panel with neutral copy. The backend mirrors this with
 * always-200 responses so we don't become an account-existence
 * oracle. The copy after submit is deliberately phrased as "if
 * there's an account, an email is on the way" to communicate the
 * uncertainty without exposing it as a state.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? 'request failed — please try again';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Stack spacing={3}>
        <Box
          component="img"
          src="/logo.svg"
          alt="10NEWTONS"
          sx={{ display: 'block', mx: 'auto', width: 'min(100%, 360px)' }}
        />
        <Typography variant="h4">Reset your password</Typography>
        {submitted ? (
          <Alert severity="success">
            If an account exists for that address, we've sent a reset
            link. Check your inbox (and spam folder). The link expires
            in 1 hour.
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} autoComplete="on">
            <Stack spacing={2}>
              <Typography color="text.secondary">
                Enter the email address you sign in with. We'll send
                you a magic link to set a new password.
              </Typography>
              <TextField
                label="Email"
                type="email"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={submitting}
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || email.trim() === ''}
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </Button>
            </Stack>
          </Box>
        )}
        <Typography variant="body2" color="text.secondary">
          <MuiLink component={Link} to="/login">
            Back to sign in
          </MuiLink>
        </Typography>
      </Stack>
    </Container>
  );
}
